import { useQuery } from "@tanstack/react-query";
import axiosInstance from "@/lib/axios";
import { switchUserCandidatesPath } from "@/config/endpoints";

/** Wire shape of GET /auth/switch-user/candidates (server: SwitchUserCandidate). */
export interface SwitchUserCandidate {
  id: number;
  name: string;
  email: string;
  avatar: string | null;
  isActive: boolean;
  role: { id: number; name: string; hierarchy: number } | null;
  branches: { id: number; name: string }[];
}

export const SWITCH_USER_CANDIDATES_KEY = ["switch-user-candidates"] as const;

/**
 * Everyone the signed-in user may switch into — roles under theirs (the role
 * wizard's Exclude toggles honoured), sharing one of their branches; every
 * non-admin for tenant admins. The server is the single judge; this list is
 * the same query the switch itself re-runs.
 *
 * One small request per session: the avatar menu shows "Switch user" only
 * when this is non-empty, and the dialog renders from the same cache.
 */
export default function useSwitchUserCandidates(enabled = true) {
  return useQuery<SwitchUserCandidate[]>({
    queryKey: SWITCH_USER_CANDIDATES_KEY,
    queryFn: async () => {
      const res = await axiosInstance.get(switchUserCandidatesPath);
      const rows = res.data?.users;
      return Array.isArray(rows) ? (rows as SwitchUserCandidate[]) : [];
    },
    enabled,
    staleTime: 5 * 60_000,
    gcTime: 15 * 60_000,
  });
}
